import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
//Modal
import { StandardDialogActions } from '../Modals/StandardDialog';
import StandardDialog from '../Modals/StandardDialog';
import ReviewsIcon from '@mui/icons-material/Reviews';
import SearchIcon from '@mui/icons-material/Search';
import LocalLibraryIcon from '@mui/icons-material/LocalLibrary';
// Services
import { RESOURCE_CATEGORY_TITLES, downloadAllPolicies, getResourceDocumentDownload, postDownloadPartnerResource } from '../../services/partnerResources';
// Material-UI
import { Box, Button, CircularProgress, Grid, List, ListItem, ListItemText, TextField, Typography } from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import { downloadBlobResponse } from '../DownloadReport/downloadUtils';

import PartnerResource from './PartnerResource';
import MPABestPractices from './Library/MPABestPractices';
import StandardLibrary from './Library/StandardLibrary';

import KeyTermsAndDefinitions from './Library/KeyTermsAndDefinitions';
import { PartnerResource as PartnerResourceInterface } from '../../interfaces/partnerResources.interface';
import { getHelpURL } from '../../services/user';
import { useAppSelector } from '../../app/hooks';
import MemberDiscounts from './Library/MemberDiscounts';

export default function PartnerResourcesModal({
  partnerResources,
  showResourceModal,
  fetchResources,
  handleCloseResourceModal,
  preventUrlChange = false,
  previewCategory,
}: {
  partnerResources: PartnerResourceInterface[],
  showResourceModal: boolean,
  handleCloseResourceModal: () => void,
  fetchResources: () => void,
  preventUrlChange?: boolean,
  previewCategory?: string,
}) {
  const { POLICIES, KEY_TERMS, BEST_PRACTICES, MEMBER_DISCOUNTS } = RESOURCE_CATEGORY_TITLES;
  const [expandedSection, setExpandedSection] = useState<string | null>(previewCategory || POLICIES);
  const [partnerResource, setPartnerResource] = useState<any | null>(null);
  const { resourceSection, documentId } = useParams();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [downloadingAll, setDownloadingAll] = useState(false);
  const iyunoLogo = `${process.env.PUBLIC_URL}/assets/Iyuno_Logo_RGB_BLK.jpg`;

  const { user } = useAppSelector(state => state.user);

  const handleSetPartnerResource = useCallback((resource: PartnerResourceInterface | null) => {
    setPartnerResource(resource);
  }, []);

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    // Must go back to the main resource page if a search query is entered
    if (partnerResource !== null) setPartnerResource(null);
    setSearchQuery(event.target.value.toLowerCase());
  };

  const resourceSections = useMemo(() => partnerResources.reduce((acc, resource) => {
    if (!acc[resource.category.title]) acc[resource.category.title] = [];
    acc[resource.category.title.toLocaleUpperCase()].push(resource);
    return acc;
  }, {
    POLICIES: [],
  } as { [key: string]: PartnerResourceInterface[] }), [partnerResources]);

  // Filter sections based on the search query
  const filteredSections =
    expandedSection && resourceSections[expandedSection]
      ? resourceSections[expandedSection].filter((item) => {
        const lowerSearchQuery = searchQuery.toLowerCase();

        if (expandedSection === BEST_PRACTICES) {
          const nativeName = item.subDescription?.toLowerCase() || '';
          return nativeName.includes(lowerSearchQuery);
        }
        return item.title.toLowerCase().includes(lowerSearchQuery) || item.subDescription.toLowerCase().includes(lowerSearchQuery);
      })
      : [];

  const handleSectionClick = useCallback((section: string) => {
    setExpandedSection(section);
    if (preventUrlChange) return;

    navigate(`/partner-resources/${section}`);
  }, [preventUrlChange, navigate]);

  const handleCloseEduCenter = () => {
    handleCloseResourceModal();
  };

  const handleUpdateResource = useCallback((resource: PartnerResourceInterface) => {
    setPartnerResource(resource);
  }, []);

  const handleBackClick = useCallback(() => {
    if (preventUrlChange) {
      setPartnerResource(null);
      return;
    }

    setPartnerResource(null);
    navigate(`/partner-resources/${expandedSection}`);
  }, [documentId, expandedSection]);

  useEffect(() =>  {
    fetchResources();
  }, [documentId, partnerResource]);

  useEffect(() => {
    if (showResourceModal) {
      setExpandedSection(previewCategory || POLICIES);
      setPartnerResource(null);
      setSearchQuery('');
    } else if (!preventUrlChange) {
      navigate('/partner-resources');
    }
    return () => {
      setSearchQuery('');
    };
  }, [showResourceModal]);

  useEffect(() => {
    if (documentId && expandedSection && resourceSections[expandedSection]) {
      // If current url is /partner-resources exit early
      if (window.location.pathname === '/partner-resources') return;
      const selectedDoc = resourceSections[expandedSection].find((doc) => doc.id === parseInt(documentId));
      if (selectedDoc) setPartnerResource(selectedDoc);
    }

    if (!documentId && partnerResource) setPartnerResource(null);
  }, [documentId, expandedSection, showResourceModal]);

  useEffect(() => {
    if (resourceSection && window.location.pathname !== '/partner-resources') {
      setExpandedSection(resourceSection);
    }
  }, [resourceSection]);

  const handleDownloadAllPolicies = useCallback(async () => {
    setDownloadingAll(true);
    try {
      const response = await downloadAllPolicies();
      downloadBlobResponse(response, 'TPN-Partner-Resource-Policies.zip');
    } catch (error) {
      console.error('Download all policies failed:', error);
    } finally {
      setDownloadingAll(false);
    }
  }, []);

  const handleDownloadPartnerResource = useCallback(async (resource: PartnerResourceInterface) => {
    if (!resource.id || !user) return;

    try {
      await postDownloadPartnerResource({
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        downloadedByUser: user.id,
        resource: resource.id as number,
      });

      if (resource.category.title === RESOURCE_CATEGORY_TITLES.MEMBER_DISCOUNTS) return;

      const download = getResourceDocumentDownload(resource);
      if (!download) return;

      const link = document.createElement('a');
      link.href = download.url;
      link.setAttribute('download', download.filename);
      link.setAttribute('target', '_blank');
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Download failed:', error);
    }
  }, [partnerResource, user, handleUpdateResource]);

  return (
        <StandardDialog
          maxWidth="xl"
          title={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <LocalLibraryIcon fontSize="small" />
              Partner Resource Center
            </Box>
          }
          isOpen={showResourceModal}
          handleClose={handleCloseEduCenter}>
        <Grid container sx={{ minHeight: '470px' }}>
            <Grid item xs={2.5} sx={{ borderRight: '1px solid #f1f4f6', padding: 2 }}>
                <Typography variant="h6">Library</Typography>
                <List component="nav">
                <ListItem
                        button
                        disabled={Boolean(preventUrlChange && previewCategory && previewCategory !== POLICIES)}
                        onClick={() => handleSectionClick(POLICIES)}
                        sx={{
                          borderRadius: '4px',
                          backgroundColor: expandedSection === POLICIES ? '#ECEFF1' : 'inherit',
                          '&:hover': {
                            backgroundColor: '#f1f4f6',
                          },
                        }}
                    >
                      <ListItemText primary="Policies" />
                  </ListItem>
                  <ListItem
                        button
                        disabled={Boolean(preventUrlChange && previewCategory && previewCategory !== MEMBER_DISCOUNTS)}
                        onClick={() => handleSectionClick(MEMBER_DISCOUNTS)}
                        sx={{
                          borderRadius: '4px',
                          backgroundColor: expandedSection === MEMBER_DISCOUNTS ? '#ECEFF1' : 'inherit',
                          '&:hover': {
                            backgroundColor: '#f1f4f6',
                          },
                        }}
                    >
                      <ListItemText primary="Member Group Discounts" />
                    </ListItem>
                  <ListItem
                      button
                      disabled={Boolean(preventUrlChange && previewCategory && previewCategory !== BEST_PRACTICES)}
                      onClick={() => handleSectionClick(BEST_PRACTICES)}
                      sx={{
                        borderRadius: '4px',
                        backgroundColor: expandedSection === BEST_PRACTICES ? '#ECEFF1' : 'inherit',
                        '&:hover': {
                          backgroundColor: '#f1f4f6',
                        },
                      }}
                  >
                      <ListItemText primary="MPA Best Practices Language Translations" />
                    </ListItem>
                    <ListItem
                        button
                        disabled={Boolean(preventUrlChange && previewCategory && previewCategory !== KEY_TERMS)}
                        onClick={() => handleSectionClick(KEY_TERMS)}
                        sx={{
                          borderRadius: '4px',
                          backgroundColor: expandedSection === KEY_TERMS ? '#ECEFF1' : 'inherit',
                          '&:hover': {
                            backgroundColor: '#f1f4f6',
                          },
                        }}
                    >
                        <ListItemText primary="Key Terms & Definitions" />
                    </ListItem>
                </List>
            </Grid>
            <Grid item xs={9.5} sx={{ padding: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                    <TextField
                        variant="outlined"
                        placeholder="Search by title or description..."
                        value={searchQuery}
                        onChange={handleSearchChange}
                        InputProps={{
                          startAdornment: <SearchIcon />,
                          sx: { height: '45px' },
                        }}
                        sx={{
                          width: '50%',
                          minWidth: '300px',
                          boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.1)',
                          '& .MuiOutlinedInput-root': {
                            '& fieldset': {
                              borderColor: '#e8e8e8',
                            },
                            '&:hover fieldset': {
                              borderColor: 'transparent',
                            },
                            '&.Mui-focused fieldset': {
                              borderColor: 'transparent',
                            },
                          },
                          borderRadius: '4px',
                        }}
                    />
                    <a target="_blank" href={getHelpURL(user?.type)} rel="noreferrer">
                        <Button
                            variant="text"
                            startIcon={<ReviewsIcon />}
                            sx={{ ml: 2, px: '20px' }}
                            onClick={()=> { return false; }}
                        >
                            Feedback
                        </Button>
                    </a>
                </Box>
                { (!documentId && !partnerResource) && (

                  <Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, px: 1 }}>
                      <Typography variant="h6" sx={{ fontWeight: 500 }}>
                        {expandedSection?.split(' ').slice(0, 7).map((word, index) =>
                          expandedSection === BEST_PRACTICES
                            ? index === 0
                              ? word.toUpperCase()
                              : word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
                            : word.charAt(0).toUpperCase() + word.slice(1).toLowerCase(),
                        ).join(' ')}
                      </Typography>
                      {expandedSection === POLICIES && filteredSections.length > 0 && (
                        <Button
                          variant="contained"
                          color="primary"
                          disabled={downloadingAll}
                          onClick={handleDownloadAllPolicies}
                          startIcon={downloadingAll ? <CircularProgress size={16} color="inherit" /> : <DownloadIcon />}
                        >
                          Download All
                        </Button>
                      )}
                    </Box>

                    <Grid container spacing={3}>
                      {expandedSection === BEST_PRACTICES ? (
                        <MPABestPractices
                          filteredSections={filteredSections}
                          iyunoLogo={iyunoLogo}
                          handleDownloadPartnerResource={handleDownloadPartnerResource}
                        />
                      ) : expandedSection === KEY_TERMS ? (
                        <KeyTermsAndDefinitions
                          filteredSections={filteredSections}
                          handleDownloadPartnerResource={handleDownloadPartnerResource}
                        />
                      ) : expandedSection === MEMBER_DISCOUNTS ? (
                        <MemberDiscounts
                          filteredSections={filteredSections}
                          handleDownloadPartnerResource={handleDownloadPartnerResource}
                          />
                      ) : (
                        <StandardLibrary
                          isPreview={preventUrlChange}
                          handleSetPartnerResource={handleSetPartnerResource}
                          expandedSection={expandedSection}
                          filteredSections={filteredSections}
                        />
                      )}
                    </Grid>
                  </Box>
                )}
                { partnerResource && (
                  <PartnerResource handleUpdateResource={handleUpdateResource} partnerResource={partnerResource} handleBackClick={handleBackClick} />
                )}
            </Grid>
        </Grid>
        <StandardDialogActions>
            <Button variant="outlined" onClick={handleCloseResourceModal}>Close</Button>
        </StandardDialogActions>
    </StandardDialog>
  );
}